import LoadingButton from "@mui/lab/LoadingButton";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField,
  Typography, useTheme
} from "@mui/material";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Employee, Plan, PlanReference, Tier } from "../../models";
import { useData } from "../../utils/useData";
import { formatCurrency } from "../../utils/formatters";
import { NavBackButton } from "./NavBackButton";
import { SideDrawerTitle } from "../shared/SideDrawer";
import { toTitleCase } from "../../utils/toTitleCase";
import { AccountKind } from "../../models/accounts/accountKind";

type AssignToPlanProps = {
  handleClose: () => void;
  selectedEmployees: Employee[];
  onSelectPlan: (selectedPlan: PlanReference) => void;
  isLoading: boolean;
  apiError: string;
  shouldHideBackButton?: boolean
};

export const SelectPlan = ({
                             handleClose,
                             selectedEmployees,
                             onSelectPlan,
                             isLoading,
                             apiError,
                             shouldHideBackButton = false
                           }: AssignToPlanProps) => {
  const theme = useTheme();
  const { plans, accounts } = useData();
  const [error, setError] = useState<string>("");
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedTier, setSelectedTier] = useState<Tier | undefined>(undefined);
  const [employeesWithSelectedPlan, setEmployeesWithSelectedPlan] = useState<Employee[]>([]);
  const [acknowledgeDuplicatePlan, setAcknowledgeDuplicatePlan] = useState<boolean>(false);
  const DEFAULT_VALUE = "-1";

  async function handleSubmit(event: FormEvent) {
    if (!selectedPlan || !selectedTier) {
      setError("Need to select Plan and Tier");
      return;
    }
    if (employeesWithSelectedPlan.length > 0 && !acknowledgeDuplicatePlan) {
      setError("You must acknowledge the possibility of assigning the same plan again.");
      return;
    }
    setError("");
    event.preventDefault();
    const planReference: PlanReference = {
      planId: selectedPlan!.id,
      tierId: selectedTier!.id!,
      employerModuleOptions: selectedPlan!.employerModuleOptions
    };
    onSelectPlan(planReference);
  }

  const assignablePlans = useMemo(() => {
    const eligiblePlans = (plans.data?.items ?? []).filter(p => p.accountKind !== AccountKind.REWARDS_RECEIVING);
    const sortedPlans = eligiblePlans.sort((a, b) => a.employerModuleOptions.employerModuleType.localeCompare(b.employerModuleOptions.employerModuleType))
    return sortedPlans;
  }, [plans.data?.items]);
  

  useEffect(() => {
    if (selectedPlan) {
      const employeesWithSelectedPlan = selectedEmployees
        .filter(e => e.accounts.find(a => a.planId === selectedPlan.id));
      // Todo: This can be made significantly more efficient by ensuring employee.Accounts is cleaned when an account is deleted, IF the employer has no more accounts of that plan (Duplicates are permitted)
      const employeesWithActiveSelectedPlan = 
        employeesWithSelectedPlan.filter(e => 
          e.accounts.find(
            a => a.planId === selectedPlan.id && 
              accounts.data?.items.find(x => x.id === a.id)?.deletedAt === null));
      setEmployeesWithSelectedPlan(employeesWithActiveSelectedPlan);
    } else {
      setEmployeesWithSelectedPlan([]);
    }
  }, [selectedEmployees, selectedPlan]);

  return (
    <>
      {!shouldHideBackButton && <NavBackButton onBackPress={handleClose} /> }
      <Box component={"form"} onSubmit={handleSubmit} maxWidth="md">
        <Box display="flex" justifyContent="space-between">
          <SideDrawerTitle sx={{ paddingLeft: 2}}>
            Assign {selectedEmployees.length} user{selectedEmployees.length > 1 ? "s" : ""} to account(s)
          </SideDrawerTitle>
        </Box>
        <Box padding={2}>
          <Grid container spacing={2}>
            {apiError && (
              <Grid item xs={12}>
                <Typography color="error">{apiError}</Typography>
              </Grid>
            )}
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            <Grid item container spacing={2}>
              <Grid item xs={12}>
                <Typography my={1}>Account to Assign</Typography>
                <Autocomplete
                  size="small"
                  options={assignablePlans}
                  getOptionLabel={(option) => option.name}
                  groupBy={(option) => toTitleCase(option.employerModuleOptions.employerModuleType)}
                  value={selectedPlan}
                  onChange={(_, newValue) => {
                    setSelectedPlan(newValue);
                  }}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Account to Assign" />
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          paddingLeft: 1,
                          paddingTop: 1,
                          fontSize: 14
                        }}
                      >
                        {params.group}
                      </Typography>
                      <ul style={{ paddingLeft: 20, margin: 0 }}>
                        {params.children}
                      </ul>
                    </li>
                  )}      
                />
              </Grid>
            </Grid>
            {selectedPlan &&
              <Grid item xs={12}>
                <InputLabel>Tier</InputLabel>
                <Select
                  key={selectedPlan.id}
                  labelId={selectedPlan.id}
                  value={selectedTier?.id ?? DEFAULT_VALUE}
                  onChange={(e: SelectChangeEvent) =>
                    setSelectedTier(selectedPlan!.tiers.find(x => x.id === e.target.value))
                  }
                  fullWidth
                  required
                  size="small"
                  variant="outlined"
                >
                  {[
                    ...selectedPlan.tiers.map((tier, index) => (
                      <MenuItem value={tier.id} key={index}>
                        {tier.name} {formatCurrency(tier.amount.amount)}
                      </MenuItem>
                    )),
                    <MenuItem value={DEFAULT_VALUE} defaultChecked key={9999}>
                      Not selected ($0)
                    </MenuItem>
                  ]}
                </Select>
              </Grid>
            }

            {employeesWithSelectedPlan.length > 0 &&
              <Grid item xs={12}>
                <Typography color={theme.palette.error.main} sx={{ mb: 2 }}>
                  {employeesWithSelectedPlan.length} user{employeesWithSelectedPlan.length > 1 ? "s" : ""} out of {selectedEmployees.length} already have this plan assigned.
                </Typography>
                <Typography color={theme.palette.error.main} sx={{ mb: 2 }}>
                  Assigning this account again will add another instance of the account for these users.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acknowledgeDuplicatePlan}
                        onChange={(e) => setAcknowledgeDuplicatePlan(e.target.checked)}
                      />
                    }
                    label="I acknowledge that this account will be assigned again, and users who already have it will receive an additional instance."
                  />
                </Box>
              </Grid>
            }
          </Grid>
        </Box>
        <Box padding={2}>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            loading={isLoading}
            disabled={!selectedPlan || !selectedTier || (employeesWithSelectedPlan.length > 0 && !acknowledgeDuplicatePlan)}
          >
            Save
          </LoadingButton>
        </Box>
      </Box>
    </>
  );
};
